import React, { useContext, useEffect, useState } from 'react'
import { Box, Flex, usePrevious } from '@chakra-ui/react'
import { Column } from 'react-table'
import { Trans, t } from '@lingui/macro'
import { PagePanel } from '../../../components/common/page/Page'

import GenericAccessModal, {
  AccessModalMode
} from '../../manage-pathways-page/components/GenericAccessModal'
import IconBan from '../../../components/common/icon/IconBan'
import IconButton from '../../../components/common/form/button/IconButton'
import IconShare from '../../../components/common/icon/IconShare'
import TableExtendedOld, {
  DEFAULT_PAGE_LIMIT
} from '../../../components/common/table-extended-old/TableExtendedOld'
import UserAvatar from '../../../components/common/user/user-avatar/UserAvatar'
import { ButtonPrimary } from 'front-end/src/components'
import { LockingModal } from './LockingModal'
import { ManageCertificationPageContext } from '../manage/ManageCertificationPage'
import {
  useRemoveUserFromCertificationMutation,
  useShareCertificationWithUsersMutation,
  useShareCertificationWithGroupsMutation,
  useAccessCertificationUsersQuery,
  useCertificationIsSharingCompletedQuery
} from './CertificationAccess.codegen'
import { brandColors } from 'shared/basicPalette'
import { fontWeights } from 'front-end/src/components/chakraThemeExtension'
import {
  SqlOrderDirectionEnum,
  UserModelPublicBaseFilterEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import useInterval from 'use-interval'

export const CertificationAccess = () => {
  const { certification } = useContext(ManageCertificationPageContext)
  const [showModal, setShowModal] = useState(false)
  const [showLockingModal, setShowLockingModal] = useState(false)
  const { data: sharingCompletedData, refetch: refetchSharingCompleted } =
    useCertificationIsSharingCompletedQuery({
      variables: {
        id: certification.id
      }
    })
  const [removeUser] = useRemoveUserFromCertificationMutation()
  const [shareWithUsers] = useShareCertificationWithUsersMutation()
  const [shareWithGroups] = useShareCertificationWithGroupsMutation()

  const openShareModal = () => {
    setShowModal(true)
  }
  const [tableFilters, setTableFilters] = useState({
    page: 1,
    order: { name: 'display_name', sort: SqlOrderDirectionEnum.ASC },
    filters: {
      name: UserModelPublicBaseFilterEnum.display_name,
      value: ''
    }
  })

  const {
    data,
    loading,
    refetch: refetchAccess
  } = useAccessCertificationUsersQuery({
    variables: {
      id: certification.id,
      offset: (tableFilters.page - 1) * DEFAULT_PAGE_LIMIT,
      limit: DEFAULT_PAGE_LIMIT,
      order: tableFilters.order,
      filters: tableFilters.filters
    }
  })

  const tableColumns: Column[] = [
    {
      Header: '',
      sortable: false,
      accessor: 'avatar',
      width: 50,
      Cell: (row) => {
        return <UserAvatar user={row.original} />
      }
    },
    {
      id: 'display_name',
      Header: t`Name`,
      accessor: 'display_name',
      Cell: (row) => {
        return (
          <Flex flexDirection="column">
            <Box color={brandColors.grey.dark} fontWeight={fontWeights.bold}>
              {row.original.display_name}
            </Box>
            <Box color={brandColors.grey[400]} fontSize="12px">
              {row.original.email}
            </Box>
          </Flex>
        )
      }
    },
    {
      Header: '',
      sortable: false,
      accessor: 'actions',
      width: 65,
      Cell: (row) => {
        const userId = row.original.id
        return (
          <IconButton
            icon={<IconBan />}
            hint={t`Remove access`}
            onClick={async () => {
              await removeUser({
                variables: {
                  id: certification.id,
                  userId
                }
              })
              await refetchAccess()
            }}
          />
        )
      }
    }
  ]

  const users = data ? data.admin?.certification?.users : []
  const usersCount = data ? data.admin?.certification?.usersCount : 0
  const pagesCount = Math.ceil((usersCount || 0) / DEFAULT_PAGE_LIMIT)

  const isSharingCompleted =
    sharingCompletedData?.admin?.certification?.isSharingCompleted
  const prevSharingCompleted = usePrevious(isSharingCompleted)

  useInterval(
    () => {
      refetchSharingCompleted()
    },
    isSharingCompleted ? null : 3000 // when null is passed, no interval is scheduled
  )

  useEffect(() => {
    if (isSharingCompleted && prevSharingCompleted === false) {
      refetchAccess()
    }
  }, [isSharingCompleted])

  return (
    <PagePanel>
      <Box className="ManageCertification__AccessTab" p="0 16px">
        <TableExtendedOld
          tableHeader={<Trans>People</Trans>}
          tableDescription={
            <Trans>
              The following people have individual access to this certification
            </Trans>
          }
          loading={loading}
          manual
          data={users}
          columns={tableColumns}
          className="-highlight AnalyticsTable"
          defaultPageSize={DEFAULT_PAGE_LIMIT}
          showPageSizeOptions={false}
          showSearchFilter
          extendFilter={
            <ButtonPrimary
              ml="8px"
              disabled={!isSharingCompleted}
              isLoading={!isSharingCompleted}
              onClick={() => {
                if (certification.locked_at) {
                  openShareModal()
                } else {
                  setShowLockingModal(true)
                }
              }}
            >
              <IconShare mr="8px" />
              {certification.locked_at ? t`Share` : t`Lock & share`}
            </ButtonPrimary>
          }
          resizable={false}
          minRows={1}
          placeholder={t`No users`}
          pages={pagesCount}
          onPageChange={(newPage) => {
            setTableFilters((prevState) => {
              return {
                ...prevState,
                page: newPage + 1
              }
            })
          }}
          page={tableFilters.page - 1}
          defaultSortDesc
          onSortedChange={(newSorted) => {
            setTableFilters((prevState) => {
              return {
                ...prevState,
                order: {
                  name: newSorted[0].id,
                  sort: newSorted[0].desc
                    ? SqlOrderDirectionEnum.DESC
                    : SqlOrderDirectionEnum.ASC
                }
              }
            })
          }}
          onSearchChange={(search) => {
            setTableFilters((prevState) => {
              return {
                ...prevState,
                filters: {
                  name: UserModelPublicBaseFilterEnum.display_name,
                  value: search
                }
              }
            })
          }}
        />
        <Box color={brandColors.grey[400]}>
          <Trans>{usersCount} Found</Trans>
        </Box>

        <LockingModal
          certificationId={certification.id}
          show={showLockingModal}
          onConfirm={() => {
            setShowLockingModal(false)
            openShareModal()
          }}
          onCancel={() => {
            setShowLockingModal(false)
          }}
        />

        <GenericAccessModal
          show={showModal}
          mode={AccessModalMode.ENROL_AND_ACCESS}
          shareWithGroups={async (variables) => {
            await shareWithGroups({
              variables: {
                id: certification.id,
                ...variables
              }
            })
            refetchSharingCompleted()
            refetchAccess()
          }}
          shareWithUsers={async (variables) => {
            await shareWithUsers({
              variables: {
                id: certification.id,
                ...variables
              }
            })
            refetchSharingCompleted()
            refetchAccess()
          }}
          onCancel={() => setShowModal(false)}
          onSubmitted={async () => {
            setShowModal(false)
          }}
          header={t`Share this certificate`}
        />
      </Box>
    </PagePanel>
  )
}
